import { computed, inject, Injectable } from '@angular/core';
import {
  ConfigurationService,
  ContextService,
  FeatureService,
  AIFeatures,
  AI_CONFIG_DEFAULT_USER_DATA,
  AI_CONFIG_DEFAULT_ADMIN_DATA,
  AiUserDataGroup,
  AIAdminData,
  AIUserData,
  withAiAdminDataDefaults,
  withAiUserDataDefaults,
} from '@maximizer/core/shared/data-access';
import {
  ConfigurationSetting,
  ConfigurationSettingCodes,
  Session,
} from '@maximizer/core/shared/domain';
import {
  map,
  tap,
  Observable,
  catchError,
  defer,
  finalize,
  of,
  switchMap,
  forkJoin,
} from 'rxjs';
import { AIStore } from '../state/ai.store';
import { isOnForUser } from '../utils/ai-access';
import { parseIqBoostConfigurationRecord } from '../utils/iqboost-configuration-record';
import { SaveQueue } from '../utils/save-queue';
import {
  CODE_3_AI_ENABLED,
  CODE_3_AI_USER_DATA,
  CODE_4_AI_ENABLED,
  CODE_4_AI_USER_DATA,
  CODE_ADMIN,
  DefaultDataScope,
  IQBOOST_V2_FEATURE_FLAG,
  IQBoostSavedConfigurationAdmin,
  IQBoostSavedConfigurationUser,
  IQDataProcessingFeatures,
  IQDataProcessingFeaturesGroup,
  ProcessingFeaturesByRow,
  ViewType,
} from '../classes/iqBoost';
import {
  createDefaultIqBoostAdminSavedConfiguration,
  createDefaultIqBoostUserSavedConfiguration,
  IQBoostViewStore
} from '../state/iqboost-view.store';
import { LANGGRAPH_CONFIG } from '../types/injection-token';

type SaveTarget = 'userData' | 'adminData' | 'enabled' | 'adminProcessing' | 'userProcessing';

interface KeyedSave<T> {
  target: 'userData' | 'adminData';
  key: string;
  read: () => T;
  write: (value: T) => void;
  value: T;
}

function parseStoredJson<T>(textValue: string | null | undefined): T | null {
  return textValue ? (JSON.parse(textValue) as T) : null;
}

const STORE_BACKED_TARGETS: readonly SaveTarget[] = ['userData', 'adminData'];

export type IqBoostRecordChange<T extends IQBoostSavedConfigurationAdmin> = Partial<Omit<T, 'dataProcessingFeatures'>> & {
  dataProcessingFeatures?: Partial<IQDataProcessingFeatures>;
};

@Injectable()
export class AiFeaturesService {
  enableSetting: ConfigurationSetting | null = null;
  aiUserDataSetting: ConfigurationSetting | null = null;
  aiAdminDataSetting: ConfigurationSetting | null = null;
  session: Session | null = null;

  readonly enabledSettingCodes: ConfigurationSettingCodes = {
    code3: CODE_3_AI_ENABLED,
    code4: CODE_4_AI_ENABLED,
  };
  readonly aiUserDataSettingsCodes: ConfigurationSettingCodes = {
    code3: CODE_3_AI_USER_DATA,
    code4: CODE_4_AI_USER_DATA,
  };
  readonly aiAdminDataSettingsCodes: ConfigurationSettingCodes = {
    code3: CODE_3_AI_USER_DATA,
    code4: CODE_4_AI_USER_DATA,
    code2: CODE_ADMIN,
  };
  readonly featureFlag = 'core-ai-features-enabled';
  readonly moduleId = 'maximizer-ai';

  isAdmin = false;
  isEnabled = false;

  private readonly saveQueues = new Map<SaveTarget, SaveQueue>();
  private readonly pendingSaves = new Map<SaveTarget, number>();
  private readonly confirmedValues = new Map<string, unknown>();
  private readonly trackedReads = new Map<string, () => unknown>();
  private readonly latestCalls = new Map<string, number>();
  private readonly unreadable = new Set<'userData' | 'adminData'>();

  showAISummarization = computed(() =>
    this.getInstalled() &&
    this.featureService.isFeatureOn('core-timeline-ai-summarization-shown', false) &&
    this.featureService.isFeatureOn('core-ai-anonymizer-enabled', false) &&
    this.isToolOnForUser('summarization')
  );

  showAIMeetingPreparation = computed(() =>
    this.getInstalled() &&
    this.featureService.isFeatureOn('core-ai-meeting-preparation-summary-shown', false) &&
    this.featureService.isFeatureOn('core-ai-anonymizer-enabled', false) &&
    this.isToolOnForUser('meetingPreparation')
  );

  showIQBoost = computed(() =>
    this.getInstalled() &&
    this.featureService.isFeatureOn('core-ai-iq-boost-shown', false) &&
    this.isToolOnForUser('iqBoost')
  );

  showIQBoostWidget = computed(() =>
    this.getInstalled() &&
    this.featureService.isFeatureOn('core-ai-iq-boost-widget-shown', true) &&
    this.isToolOnForUser('iqBoostWidget')
  );

  isSLE = computed(() =>
    this.featureService.isFeatureOn('tmp-core-ai-iq-boost-is-sle', false),
  );

  // Empty where the orchestrator is not deployed, and in core, which provides no LangGraph config.
  private readonly langGraphUrl = inject(LANGGRAPH_CONFIG, { optional: true })?.url ?? '';

  constructor(
    private readonly context: ContextService,
    private readonly configurationService: ConfigurationService,
    private readonly featureService: FeatureService,
    private readonly aiStore: AIStore,
    private readonly iqBoostStore: IQBoostViewStore,
  ) {}

  isToolOnForUser(key: keyof AIFeatures): boolean {
    const group = this.aiStore.aiUserDataGroup();
    return isOnForUser({
      orgAvailable: group.admin.aiFeaturesEnabled[key],
      userEnabled: group.user.aiFeaturesEnabled[key],
    });
  }

  getInstalled(): boolean {
    const module = this.context.modules.find((m) => m.id === this.moduleId);
    return (
      this.featureService.isFeatureOn(this.featureFlag, false) &&
      module?.overrideView?.integrationArgs !== undefined
    );
  }

  /**
   * IQ Boost v2 (the LangGraph dialogs and services) when true, classic otherwise. Read on every call.
   * Keep it to the AI app: core always opens `maximizer-ai-iqboost`, so switching needs no core deploy.
   */
  isIQBoostV2Enabled(): boolean {
    // Where no orchestrator is deployed the flag alone would open dialogs that cannot answer.
    return !!this.langGraphUrl && this.featureService.isFeatureOn(IQBOOST_V2_FEATURE_FLAG, false);
  }

  getEnabled(): Observable<boolean> {
    return this.afterPendingSaves('enabled').pipe(
      switchMap(() => this.configurationService.getSetting(this.enabledSettingCodes)),
      tap((setting) => {
        this.enableSetting = setting;
      }),
      map((setting) => setting?.textValue === 'true'),
    );
  }

  getConfiguration(session: Session): Observable<{ installed: boolean; enabled: boolean; aiUserDataGroup: AiUserDataGroup }> {
    const installed = this.getInstalled();
    this.session = session;
    this.isAdmin = session.user.roles.administrator;
    
    if (!installed) {
      const defaultGroup: AiUserDataGroup = {
        admin: AI_CONFIG_DEFAULT_ADMIN_DATA,
        user: AI_CONFIG_DEFAULT_USER_DATA,
        isAdmin: this.isAdmin
      };
      return of({ installed, enabled: false, aiUserDataGroup: defaultGroup });
    }

    const enabled$ = this.getEnabled();
    const aiUserDataGroup$ = this.getAIUserData(session);

    return forkJoin({
      enabled: enabled$,
      aiUserDataGroup: aiUserDataGroup$,
    }).pipe(
      tap(({ enabled, aiUserDataGroup }) => {
        this.aiStore.setUserDataGroup(aiUserDataGroup);
        this.isEnabled = enabled;
      }),
      map(({ enabled, aiUserDataGroup }) => ({
        installed,
        enabled,
        aiUserDataGroup,
      })),
    );
  }

  getAIUserData(session: Session): Observable<AiUserDataGroup> {
    this.session = session;
    this.isAdmin = session.user.roles.administrator;
    const adminCodes = { ...this.aiUserDataSettingsCodes, code2: CODE_ADMIN };
    const userCodes = { ...this.aiUserDataSettingsCodes, code2: session.user.id };

    if (this.hasPendingStoreSaves()) {
      return of({ ...this.aiStore.aiUserDataGroup(), isAdmin: this.isAdmin });
    }

    this.confirmedValues.clear();
    this.trackedReads.clear();

    return forkJoin({
      admin: this.configurationService.getSetting(adminCodes).pipe(
        map((setting) => {
          this.aiAdminDataSetting = setting;
          this.unreadable.delete('adminData');
          return withAiAdminDataDefaults(parseStoredJson<AIAdminData>(setting?.textValue));
        }),
        catchError(() => {
          this.unreadable.add('adminData');
          return of(withAiAdminDataDefaults(null));
        }),
      ),
      user: this.configurationService.getSetting(userCodes).pipe(
        map((setting) => {
          this.aiUserDataSetting = setting;
          this.unreadable.delete('userData');
          return withAiUserDataDefaults(parseStoredJson<AIUserData>(setting?.textValue));
        }),
        catchError(() => {
          this.unreadable.add('userData');
          return of(withAiUserDataDefaults(null));
        }),
      ),
    }).pipe(map(({ admin, user }) => ({ admin, user, isAdmin: this.isAdmin })));
  }

  hasUnreadableSettings(): boolean {
    return this.unreadable.size > 0;
  }

  processFiltersConfiguration(
    session: Session,
    updateViewType = false,
  ): Observable<IQDataProcessingFeaturesGroup> {
    this.iqBoostStore.configurationSettingCodesUser().code2 = session.user.id;

    return forkJoin({
      user: this.configurationService.getSetting(this.iqBoostStore.configurationSettingCodesUser())
        .pipe(
          map((setting) => {
            const configuration: IQBoostSavedConfigurationUser = setting?.textValue
              ? JSON.parse(setting.textValue)
              : createDefaultIqBoostUserSavedConfiguration();
            this.iqBoostStore.setConfigurationSettingUser(setting);
            this.iqBoostStore.setDataScope(configuration.dataScope ?? DefaultDataScope);

            if (updateViewType) {
              this.iqBoostStore.setViewType(configuration.wasFirstTimeOpenedPrivacySecurityCenter
                ? ViewType.Report
                : ViewType.PrivacySecurityCenter
              );
            }

            return configuration;
          }),
        ),
      admin: this.configurationService.getSetting(this.iqBoostStore.configurationSettingCodesAdmin())
        .pipe(
          map((setting) => {
            const configuration: IQBoostSavedConfigurationAdmin = setting?.textValue
              ? JSON.parse(setting.textValue)
              : createDefaultIqBoostAdminSavedConfiguration();
            this.iqBoostStore.setConfigurationSettingAdmin(setting);
            return configuration;
          }),
        ),
    }).pipe(
      catchError(() =>
        of({
          user: createDefaultIqBoostUserSavedConfiguration(),
          admin: createDefaultIqBoostAdminSavedConfiguration(),
        }).pipe(
          tap(() => {
            this.iqBoostStore.setConfigurationSettingUser(null);
            this.iqBoostStore.setConfigurationSettingAdmin(null);
            this.iqBoostStore.setDataScope(DefaultDataScope);
            if (updateViewType) {
              this.iqBoostStore.setViewType(ViewType.PrivacySecurityCenter);
            }
          }),
        ),
      ),
      map(({ user, admin }) => ({
        user,
        admin,
        isAdmin: session.user.roles.administrator,
      })),
      map((dataProcessingFeatureGroup) =>
        this.normalizeProcessingFeatures(dataProcessingFeatureGroup)
      ),
      tap((dataProcessingFeatureGroup) =>
        this.iqBoostStore.setDataProcessingFeatureGroup(dataProcessingFeatureGroup)
      ),
    );
  }

  addCreditsUsed(creditsUsed: number): Observable<boolean> {
    const aiUserDataGroup = this.aiStore.aiUserDataGroup();
    const currentCreditsUsed = aiUserDataGroup.admin.creditsUsed;
    this.aiStore.setCreditsUsed(currentCreditsUsed + creditsUsed);
    return this.saveAdminData();
  }

  setOrganizationFeature(key: keyof AIFeatures, available: boolean): Observable<boolean> {
    if (!this.isAdmin) return of(false);
    return this.saveWithRollback({
      target: 'adminData',
      key: `feature:${key}`,
      read: () => this.aiStore.aiUserDataGroup().admin.aiFeaturesEnabled[key],
      write: (value) => this.aiStore.updateAiFeatureEnabledAdmin(key, value),
      value: available,
    });
  }

  setUserFeature(key: keyof AIFeatures, enabled: boolean): Observable<boolean> {
    return this.saveWithRollback({
      target: 'userData',
      key: `feature:${key}`,
      read: () => this.aiStore.aiUserDataGroup().user.aiFeaturesEnabled[key],
      write: (value) => this.aiStore.updateAiFeatureEnabled(key, value),
      value: enabled,
    });
  }

  private saveWithRollback<T>({ target, key, read, write, value }: KeyedSave<T>): Observable<boolean> {
    const trackingKey = `${target}:${key}`;
    if (!this.confirmedValues.has(trackingKey)) this.confirmedValues.set(trackingKey, read());
    this.trackedReads.set(trackingKey, read);
    const call = (this.latestCalls.get(trackingKey) ?? 0) + 1;
    this.latestCalls.set(trackingKey, call);
    write(value);

    const persist = () => (target === 'adminData' ? this.writeAdminData() : this.writeUserData());
    return this.enqueueSave(target, () => {
      const sentValues = this.readTrackedValues(target);
      return persist().pipe(
        tap((saved) => {
          if (saved) {
            sentValues.forEach((sentValue, sentKey) => this.confirmedValues.set(sentKey, sentValue));
          } else if (this.latestCalls.get(trackingKey) === call) {
            write(this.confirmedValues.get(trackingKey) as T);
          }
        }),
      );
    });
  }

  private readTrackedValues(target: KeyedSave<unknown>['target']): Map<string, unknown> {
    const prefix = `${target}:`;
    return new Map(
      [...this.trackedReads]
        .filter(([trackingKey]) => trackingKey.startsWith(prefix))
        .map(([trackingKey, readValue]): [string, unknown] => [trackingKey, readValue()]),
    );
  }

  private enqueueSave(target: SaveTarget, run: () => Observable<boolean>): Observable<boolean> {
    let queue = this.saveQueues.get(target);
    if (!queue) {
      queue = new SaveQueue();
      this.saveQueues.set(target, queue);
    }
    const queued = queue.enqueue(run);
    return defer(() => {
      this.changePendingSaves(target, 1);
      return queued.pipe(finalize(() => this.changePendingSaves(target, -1)));
    });
  }

  private changePendingSaves(target: SaveTarget, change: number): void {
    this.pendingSaves.set(target, (this.pendingSaves.get(target) ?? 0) + change);
  }

  private hasPendingStoreSaves(): boolean {
    return STORE_BACKED_TARGETS.some((target) => this.isSavePending(target));
  }

  private isSavePending(target: SaveTarget): boolean {
    return (this.pendingSaves.get(target) ?? 0) > 0;
  }

  private afterPendingSaves(target: SaveTarget): Observable<boolean> {
    return this.saveQueues.get(target)?.enqueue(() => of(true)) ?? of(true);
  }

  setEnabled(enabled: boolean): Observable<boolean> {
    return this.enqueueSave('enabled', () => {
      const currentSetting = this.enableSetting
        ? of(this.enableSetting)
        : this.configurationService.getSetting(this.enabledSettingCodes);

      return currentSetting.pipe(
        switchMap((setting) =>
          this.configurationService.updateSetting(
            setting?.id ?? null,
            this.enabledSettingCodes,
            enabled.toString(),
          ),
        ),
        tap((setting) => {
          this.enableSetting = setting;
        }),
        catchError(() => of(null)),
        map((setting) => (setting?.id?.length ?? 0) > 0),
      );
    });
  }

  getProcessingFeatures(): Observable<ProcessingFeaturesByRow> {
    return forkJoin({
      admin: this.readProcessingFeatures('adminProcessing', this.iqBoostStore.configurationSettingCodesAdmin(), createDefaultIqBoostAdminSavedConfiguration),
      user: this.readProcessingFeatures('userProcessing', this.iqBoostStore.configurationSettingCodesUser(), createDefaultIqBoostUserSavedConfiguration),
    });
  }

  setOrganizationProcessingFeatures(change: Partial<IQDataProcessingFeatures>): Observable<boolean> {
    if (!this.isAdmin) return of(false);
    return this.enqueueSave('adminProcessing', () =>
      this.saveIqBoostRecord(this.iqBoostStore.configurationSettingCodesAdmin(), createDefaultIqBoostAdminSavedConfiguration, { dataProcessingFeatures: change }).pipe(
        tap((saved) => this.syncProcessingStoreAfterSave(null, saved)),
        catchError(() => of(null)),
        map((saved) => (saved?.id?.length ?? 0) > 0),
      ),
    );
  }

  setUserProcessingFeatures(change: Partial<IQDataProcessingFeatures>): Observable<boolean> {
    return this.updateUserIqBoostRecord({ dataProcessingFeatures: change });
  }

  updateUserIqBoostRecord(change: IqBoostRecordChange<IQBoostSavedConfigurationUser>): Observable<boolean> {
    return this.enqueueSave('userProcessing', () =>
      this.saveIqBoostRecord(this.iqBoostStore.configurationSettingCodesUser(), createDefaultIqBoostUserSavedConfiguration, change).pipe(
        tap((saved) => this.syncProcessingStoreAfterSave(saved, null)),
        catchError(() => of(null)),
        map((saved) => (saved?.id?.length ?? 0) > 0),
      ),
    );
  }

  private readProcessingFeatures<T extends IQBoostSavedConfigurationAdmin>(
    target: SaveTarget,
    codes: ConfigurationSettingCodes,
    createDefault: () => T,
  ): Observable<IQDataProcessingFeatures> {
    return this.afterPendingSaves(target).pipe(
      switchMap(() => this.configurationService.getSetting(codes)),
      map((setting) => this.toProcessingFeatures(parseIqBoostConfigurationRecord(setting?.textValue, createDefault).dataProcessingFeatures)),
    );
  }

  private saveIqBoostRecord<T extends IQBoostSavedConfigurationAdmin>(
    codes: ConfigurationSettingCodes,
    createDefault: () => T,
    change: IqBoostRecordChange<T>,
  ): Observable<ConfigurationSetting | null> {
    return this.configurationService.getSetting(codes).pipe(
      switchMap((setting) => {
        const configuration = parseIqBoostConfigurationRecord(setting?.textValue, createDefault);
        return this.configurationService.updateSetting(
          setting?.id ?? null,
          codes,
          JSON.stringify({
            ...configuration,
            ...change,
            dataProcessingFeatures: { ...configuration.dataProcessingFeatures, ...change.dataProcessingFeatures },
          }),
        );
      }),
    );
  }

  private toProcessingFeatures(stored: Partial<IQDataProcessingFeatures> | undefined): IQDataProcessingFeatures {
    return this.applyAnonymizerFeatureFlagToFeatures({
      anonymized: stored?.anonymized === true,
      useGlobalServers: stored?.useGlobalServers === true,
      useWebData: stored?.useWebData === true,
    });
  }

  private syncProcessingStoreAfterSave(
    userSetting: ConfigurationSetting | null,
    adminSetting: ConfigurationSetting | null,
  ): void {
    if (userSetting) {
      this.iqBoostStore.setConfigurationSettingUser(userSetting);
      const userConfiguration = parseIqBoostConfigurationRecord(userSetting.textValue, createDefaultIqBoostUserSavedConfiguration);
      const current = this.iqBoostStore.dataProcessingFeatureGroup();
      this.iqBoostStore.setDataProcessingFeatureGroup(
        this.normalizeProcessingFeatures({
          ...current,
          user: { ...userConfiguration, recentUserPrompts: current.user.recentUserPrompts },
        }),
      );
    }

    if (adminSetting) {
      this.iqBoostStore.setConfigurationSettingAdmin(adminSetting);
      const adminConfiguration = parseIqBoostConfigurationRecord(adminSetting.textValue, createDefaultIqBoostAdminSavedConfiguration);
      this.iqBoostStore.setDataProcessingFeatureGroup(
        this.normalizeProcessingFeatures({ ...this.iqBoostStore.dataProcessingFeatureGroup(), admin: adminConfiguration }),
      );
    }
  }

  private applyAnonymizerFeatureFlagToFeatures(
    dataProcessingFeatures: IQDataProcessingFeatures,
  ): IQDataProcessingFeatures {
    if (this.featureService.isFeatureOn('core-ai-anonymizer-enabled', true)) {
      return dataProcessingFeatures;
    }

    return { ...dataProcessingFeatures, anonymized: true };
  }

  private saveAdminData(): Observable<boolean> {
    return this.enqueueSave('adminData', () => this.writeAdminData());
  }

  private writeAdminData(): Observable<boolean> {
    if (this.unreadable.has('adminData')) return of(false);
    const aiUserDataGroup = this.aiStore.aiUserDataGroup();
    const currentAdminData = {
      creditsUsed: aiUserDataGroup.admin.creditsUsed,
      creditsTotal: aiUserDataGroup.admin.creditsTotal,
      aiFeaturesEnabled: aiUserDataGroup.admin.aiFeaturesEnabled,
    };
    const currentSetting = this.aiAdminDataSetting
      ? of(this.aiAdminDataSetting)
      : this.configurationService.getSetting(this.aiAdminDataSettingsCodes);

    return currentSetting.pipe(
      switchMap((response) =>
        this.configurationService.updateSetting(
          response?.id ?? null,
          this.aiAdminDataSettingsCodes,
          JSON.stringify(currentAdminData),
        ),
      ),
      tap((setting) => this.aiAdminDataSetting = setting),
      catchError(() => of(null)),
      map((setting) => (setting?.id?.length ?? 0) > 0),
    );
  }

  private writeUserData(): Observable<boolean> {
    if (this.unreadable.has('userData')) return of(false);
    const aiUserDataGroup = this.aiStore.aiUserDataGroup();
    const currentUserData = {
      aiFeaturesEnabled: aiUserDataGroup.user.aiFeaturesEnabled,
    };
    const settingsCodes = { ...this.aiUserDataSettingsCodes, code2: this.session?.user.id };
    const currentSetting = this.aiUserDataSetting
      ? of(this.aiUserDataSetting)
      : this.configurationService.getSetting(settingsCodes);

    return currentSetting.pipe(
      switchMap((response) =>
        this.configurationService.updateSetting(
          response?.id ?? null,
          settingsCodes,
          JSON.stringify(currentUserData),
        ),
      ),
      tap((setting) => this.aiUserDataSetting = setting),
      catchError(() => of(null)),
      map((setting) => (setting?.id?.length ?? 0) > 0),
    );
  }

  private normalizeProcessingFeatures(
    dataProcessingFeatureGroup: IQDataProcessingFeaturesGroup,
  ): IQDataProcessingFeaturesGroup {
    return {
      ...dataProcessingFeatureGroup,
      user: {
        ...dataProcessingFeatureGroup.user,
        dataProcessingFeatures: this.toProcessingFeatures(dataProcessingFeatureGroup.user.dataProcessingFeatures),
      },
      admin: {
        ...dataProcessingFeatureGroup.admin,
        dataProcessingFeatures: this.toProcessingFeatures(dataProcessingFeatureGroup.admin.dataProcessingFeatures),
      },
    };
  }
}
